import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material'
import {
  Security as SecurityIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Key as KeyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import toast from 'react-hot-toast'
import { StandardInput, StandardButton } from './ui'
import AuthPageWrapper from './auth/AuthPageWrapper'

interface MFAChallengeProps {
  sessionToken: string
  availableMethods: Array<{ id: string; type: string; display: string }>
  onSuccess: (accessToken: string, refreshToken: string) => void
  onCancel: () => void
}

const MFAChallenge: React.FC<MFAChallengeProps> = ({
  sessionToken,
  availableMethods,
  onSuccess,
  onCancel,
}) => {
  // ✅ FIX: Găsește metoda PRIMARY sau fallback la prima metodă
  const findPrimaryOrFirstMethod = () => {
    // Caută metoda cu is_primary (poate fi transmisă din backend)
    // Nota: Backend TREBUIE să trimită is_primary în available_methods
    const primaryMethod = availableMethods.find((m: any) => m.is_primary === true)
    return primaryMethod?.id || availableMethods[0]?.id || ''
  }
  
  const [selectedMethodId, setSelectedMethodId] = useState(findPrimaryOrFirstMethod())
  const [code, setCode] = useState('')
  const [rememberDevice, setRememberDevice] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null)
  const [codeSent, setCodeSent] = useState(false)
  const [isInitialMount, setIsInitialMount] = useState(true)
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)

  const selectedMethod = availableMethods.find((m) => m.id === selectedMethodId)

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'sms':
        return <PhoneIcon />
      case 'email':
        return <EmailIcon />
      case 'totp':
        return <KeyIcon />
      default:
        return <SecurityIcon />
    }
  }

  const handleSendCode = async () => {
    if (!selectedMethod || selectedMethod.type === 'totp') return

    try {
      setLoading(true)
      setError('')
      const response = await fetch('/api/v1/auth/mfa/resend-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // CRITICAL: trimite cookies
        body: JSON.stringify({
          session_token: sessionToken,
          method_id: selectedMethodId,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(data.message || 'Cod trimis!')
      } else {
        setError(data.detail || 'Eroare la trimiterea codului')
      }
    } catch (err: any) {
      console.error('Error sending code:', err)
      setError('Eroare la trimiterea codului')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!code) {
      setError('Introduceți codul de verificare')
      return
    }

    try {
      setLoading(true)
      setError('')

      const response = await fetch('/api/v1/auth/mfa/challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // CRITICAL: primește și trimite cookies httpOnly
        body: JSON.stringify({
          session_token: sessionToken,
          method_id: useRecoveryCode ? 'recovery' : selectedMethodId,
          code: code,
          remember_device: rememberDevice,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        onSuccess(data.access_token, data.refresh_token)
      } else {
        setError(data.message || 'Cod invalid')
        if (data.remaining_attempts !== undefined) {
          setRemainingAttempts(data.remaining_attempts)
        }
      }
    } catch (err: any) {
      console.error('Error verifying MFA:', err)
      setError('Eroare la verificarea codului')
    } finally {
      setLoading(false)
    }
  }

  const handleMethodChange = (newMethodId: string) => {
    setSelectedMethodId(newMethodId)
    setCode('')
    setError('')
    setCodeSent(false)
  }

  // ✅ FIX: Auto-send code DOAR când user schimbă MANUAL metoda în dropdown
  // NU trimite la mount - backend deja a trimis cod pentru metoda primary!
  useEffect(() => {
    // Skip la primul mount - backend deja a trimis cod la metoda primary
    if (isInitialMount) {
      setIsInitialMount(false)
      // Marchează că backend deja a trimis cod pentru metoda primary (SMS/Email)
      if (selectedMethod && selectedMethod.type !== 'totp') {
        setCodeSent(true)
      }
      return
    }

    // Trimite cod DOAR când user schimbă MANUAL metoda în dropdown
    const sendCodeForNewMethod = async () => {
      if (selectedMethod && selectedMethod.type !== 'totp' && !codeSent) {
        await handleSendCode()
        setCodeSent(true)
      }
    }
    sendCodeForNewMethod()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethodId])

  return (
    <AuthPageWrapper>
      <Paper
        elevation={0}
        sx={{
          padding: 3,
          width: '100%',
          maxWidth: '380px',
          borderRadius: 8,
          background: 'transparent',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none',
          
          transition: 'border-color 0.3s ease',
          '&:hover': {
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
          
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <Box>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <SecurityIcon sx={{ fontSize: 48, color: '#FFFFFF', mb: 1, opacity: 0.9 }} />
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              color: '#FFFFFF',
              fontSize: '24px',
              fontWeight: 500,
              marginBottom: 0.5,
              letterSpacing: '0.5px'
            }}
          >
            Verificare MFA
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px'
            }}
          >
            Introduceți codul de autentificare
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
                border: '1px solid rgba(211, 47, 47, 0.3)',
                color: '#FFFFFF',
                '& .MuiAlert-icon': {
                  color: '#ff6b6b',
                }
              }}
            >
              {error}
              {remainingAttempts !== null && (
                <Typography 
                  variant="caption" 
                  display="block"
                  sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                >
                  Încercări rămase: {remainingAttempts}
                </Typography>
              )}
            </Alert>
          )}

          {/* Method Selection - ascuns când folosim recovery code */}
          {!useRecoveryCode && availableMethods.length > 1 && (
            <FormControl 
              fullWidth 
              sx={{ 
                mb: 2,
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiOutlinedInput-root': {
                  color: '#FFFFFF',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                },
                '& .MuiSelect-icon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                }
              }}
            >
              <InputLabel>Metodă MFA</InputLabel>
              <Select
                value={selectedMethodId}
                onChange={(e) => handleMethodChange(e.target.value)}
                label="Metodă MFA"
              >
                {availableMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getMethodIcon(method.type)}
                      {method.display}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Recovery Code Info */}
          {useRecoveryCode && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                color: '#FFFFFF',
                '& .MuiAlert-icon': {
                  color: '#ffb74d',
                }
              }}
            >
              Introduceți un cod de recuperare (format: XXXX-XXXX)
            </Alert>
          )}

          {/* Info message for SMS/Email */}
          {!useRecoveryCode && selectedMethod && selectedMethod.type !== 'totp' && codeSent && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                border: '1px solid rgba(33, 150, 243, 0.3)',
                color: '#FFFFFF',
                '& .MuiAlert-icon': {
                  color: '#64b5f6',
                }
              }}
            >
              Codul a fost trimis la {selectedMethod.display}
            </Alert>
          )}

          {/* Send Code Button for SMS/Email - ascuns când folosim recovery code */}
          {!useRecoveryCode && selectedMethod && selectedMethod.type !== 'totp' && (
            <Box sx={{ mb: 2 }}>
              <StandardButton
                fullWidth
                variant="ghost"
                startIcon={<RefreshIcon />}
                onClick={handleSendCode}
                disabled={loading}
              >
                Trimite cod nou
              </StandardButton>
            </Box>
          )}

          {/* Verification Code */}
          <Box sx={{ mb: 2 }}>
            <StandardInput
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={useRecoveryCode ? "Cod recuperare (ex: ABCD-1234)" : "Cod de verificare (6 cifre)"}
              autoFocus
              disabled={loading}
              startIcon={<KeyIcon />}
              type="text"
            />
          </Box>

          {/* Remember Device */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberDevice}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRememberDevice(e.target.checked)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    '&.Mui-checked': {
                      color: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                />
              }
              label="Ține minte acest dispozitiv (7 zile)"
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                }
              }}
            />
          </Box>

          {/* Submit Button */}
          <Box sx={{ mb: 2 }}>
            <StandardButton
              type="submit"
              fullWidth
              variant="ghost"
              size="large"
              disabled={loading || !code}
              loading={loading}
            >
              Verifică
            </StandardButton>
          </Box>

          {/* Cancel */}
          <Box sx={{ mb: 2 }}>
            <StandardButton 
              fullWidth 
              variant="ghost"
              onClick={onCancel} 
              disabled={loading}
              sx={{ 
                opacity: 0.6,
                '&:hover': {
                  opacity: 0.8,
                }
              }}
            >
              Anulează
            </StandardButton>
          </Box>

          {/* Recovery Code Link / Back to Methods */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link 
              component="button"
              type="button"
              variant="body2" 
              underline="hover"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                setUseRecoveryCode(!useRecoveryCode)
                setCode('')
                setError('')
              }}
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                '&:hover': {
                  color: '#FFFFFF',
                }
              }}
            >
              {useRecoveryCode ? 'Înapoi la metode MFA' : 'Folosește cod de recuperare'}
            </Link>
          </Box>
        </Box>
      </Box>
      </Paper>
    </AuthPageWrapper>
  )
}

export default MFAChallenge

